import { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { CheckCircle, Clock, TrendingUp, Play, Eye, BarChart3, FileQuestion, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import * as api from '../utils/api';

interface Quiz {
  id: string;
  title: string;
  courseId: string;
  description: string;
  timeLimit: number;
  questions: any[];
}

interface Course {
  id: string;
  name: string;
  description: string;
}

interface QuizAttempt {
  id: string;
  quizId: string;
  studentId: string;
  answers: { [questionId: string]: string } | string;
  score: number;
  totalPoints: number;
  completedAt: string;
  timeSpent: number;
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('all');
  const [difficultyData, setDifficultyData] = useState<{name: string; correct: number; total: number; percentage: number}[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  // Load difficulty data when course selection changes
  useEffect(() => {
    const loadDifficultyData = async () => {
      try {
        const data = await api.getStudentDifficultyAnalytics(selectedCourse);
        setDifficultyData(data.difficultyData || []);
      } catch (error) {
        console.error('Error loading difficulty data:', error);
      }
    };
    if (user) {
      loadDifficultyData();
    }
  }, [user, selectedCourse]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (!user) return;

      // Load data from API
      const [quizzesData, attemptsData, coursesData] = await Promise.all([
        api.getAllQuizzes(),
        api.getAttemptsByStudent(user.id),
        api.getAllCourses()
      ]);

      setQuizzes(quizzesData);
      setAttempts(attemptsData);
      setCourses(coursesData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const isQuizCompleted = (quizId: string) => {
    return attempts.some(a => a.quizId === quizId);
  };

  const getQuizAttempt = (quizId: string) => {
    return attempts.find(a => a.quizId === quizId);
  };

  // Filter data by selected course
  const filteredQuizzes = selectedCourse === 'all' 
    ? quizzes 
    : quizzes.filter(q => q.courseId === selectedCourse);
  
  const filteredAttempts = selectedCourse === 'all'
    ? attempts
    : attempts.filter(a => {
        const quiz = quizzes.find(q => q.id === a.quizId);
        return quiz && quiz.courseId === selectedCourse;
      });

  const finishedCount = filteredAttempts.length;
  const pendingCount = filteredQuizzes.length - finishedCount;

  // Total time spent
  const totalTimeSpent = filteredAttempts.reduce((acc, a) => acc + (a.timeSpent || 0), 0);
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Analytics data
  const scoreHistory = filteredAttempts
    .map((attempt) => {
      const quiz = quizzes.find(q => q.id === attempt.quizId);
      const percentage = attempt.totalPoints > 0 ? (attempt.score / attempt.totalPoints) * 100 : 0;
      return {
        name: quiz?.title?.substring(0, 15) || 'Quiz',
        score: Math.round(percentage),
        timeSpent: Math.round((attempt.timeSpent || 0) / 60), // in minutes
      };
    })
    .reverse();

  const averageScore = filteredAttempts.length > 0
    ? filteredAttempts.reduce((acc, a) => {
        const percentage = a.totalPoints > 0 ? (a.score / a.totalPoints) * 100 : 0;
        return acc + percentage;
      }, 0) / filteredAttempts.length
    : 0;

  // difficultyData is now loaded from API via useEffect

  // Time spent per quiz data
  const timeSpentData = filteredAttempts
    .map(attempt => {
      const quiz = quizzes.find(q => q.id === attempt.quizId);
      return {
        name: quiz?.title?.substring(0, 12) || 'Quiz',
        minutes: Math.round((attempt.timeSpent || 0) / 60),
      };
    })
    .reverse()
    .slice(0, 10);

  // Detailed breakdown - score distribution
  const scoreDistribution = (() => {
    const ranges = [
      { name: '90-100%', min: 90, max: 100, count: 0, color: '#22c55e' },
      { name: '80-89%', min: 80, max: 89, count: 0, color: '#84cc16' },
      { name: '70-79%', min: 70, max: 79, count: 0, color: '#eab308' },
      { name: '60-69%', min: 60, max: 69, count: 0, color: '#f97316' },
      { name: 'Below 60%', min: 0, max: 59, count: 0, color: '#ef4444' },
    ];

    filteredAttempts.forEach(attempt => {
      const percentage = attempt.totalPoints > 0 ? (attempt.score / attempt.totalPoints) * 100 : 0;
      const range = ranges.find(r => percentage >= r.min && percentage <= r.max);
      if (range) range.count++;
    });

    return ranges.filter(r => r.count > 0);
  })();

  const COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444'];

  // Get unique courses that have quizzes
  const coursesWithQuizzes = courses.filter(course => 
    quizzes.some(quiz => quiz.courseId === course.id)
  );

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  // Determine which view to show based on the current route
  const currentPath = location.pathname;
  const showQuizzesOnly = currentPath === '/student/quizzes';
  const showAnalyticsOnly = currentPath === '/student/analytics';
  const showDashboard = currentPath === '/student';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-gray-900">
          {showQuizzesOnly ? 'My Quizzes' : showAnalyticsOnly ? 'My Analytics' : 'Student Dashboard'}
        </h2>
        <p className="text-gray-600 mt-1">
          {showQuizzesOnly ? 'View and take your quizzes' : showAnalyticsOnly ? 'Track your performance and progress' : 'Track your progress and take quizzes'}
        </p>
      </div>

      {/* Course Filter Tabs - Show on Analytics page */}
      {showAnalyticsOnly && coursesWithQuizzes.length > 0 && (
        <Tabs value={selectedCourse} onValueChange={setSelectedCourse} className="w-full">
          <TabsList className="w-full justify-start flex-wrap h-auto gap-2 bg-gray-100 p-2 rounded-lg">
            <TabsTrigger 
              value="all" 
              className="w-[200px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=inactive]:hover:bg-gray-200 data-[state=inactive]:hover:text-blue-600 transition-all cursor-pointer px-4 py-2 rounded-md font-medium"
            >
              All Courses
            </TabsTrigger>
            <div className="w-px h-8 bg-gray-300 self-center mx-1" />
            {coursesWithQuizzes.map((course, index) => (
              <div key={course.id} className="flex items-center gap-2">
                <TabsTrigger 
                  value={course.id} 
                  className="w-[200px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 data-[state=inactive]:hover:bg-gray-200 data-[state=inactive]:hover:text-blue-600 transition-all cursor-pointer px-4 py-2 rounded-md font-medium"
                >
                  {course.name}
                </TabsTrigger>
                {index < coursesWithQuizzes.length - 1 && (
                  <div className="w-px h-8 bg-gray-300" />
                )}
              </div>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Progress Cards - Show on Dashboard and Analytics pages */}
      {(showDashboard || showAnalyticsOnly) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Finished Quizzes</CardTitle>
              <CheckCircle className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{finishedCount}</div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => document.getElementById('quiz-list')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Pending Quizzes</CardTitle>
              <Clock className="w-4 h-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{pendingCount}</div>
              <p className="text-xs text-gray-400 mt-1">Click to view</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Average Score</CardTitle>
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{averageScore.toFixed(1)}%</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Total Time Spent</CardTitle>
              <Timer className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{formatTime(totalTimeSpent)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Charts - Dashboard: only Score History and Score Distribution */}
      {showDashboard && filteredAttempts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Score History</CardTitle>
              <CardDescription>Your performance over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={scoreHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} fontSize={12} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} name="Score %" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Score Distribution</CardTitle>
              <CardDescription>Breakdown of your quiz scores</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={scoreDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {scoreDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Charts - Full analytics page: all 4 charts */}
      {showAnalyticsOnly && filteredAttempts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Score History</CardTitle>
              <CardDescription>Your performance over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={scoreHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} fontSize={12} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} name="Score %" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Performance by Difficulty</CardTitle>
              <CardDescription>How you perform on different difficulty levels</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={difficultyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" width={60} />
                  <Tooltip formatter={(value: number, name: string, props: any) => [
                    `${value}% (${props.payload.correct}/${props.payload.total})`,
                    'Accuracy'
                  ]} />
                  <Bar dataKey="percentage" fill="#8b5cf6" name="Accuracy %" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Score Distribution</CardTitle>
              <CardDescription>Breakdown of your quiz scores</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={scoreDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {scoreDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Time Spent Per Quiz</CardTitle>
              <CardDescription>Minutes spent on each quiz</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={timeSpentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} fontSize={12} />
                  <YAxis />
                  <Tooltip formatter={(value: number) => [`${value} min`, 'Time Spent']} />
                  <Bar dataKey="minutes" fill="#10b981" name="Minutes" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Empty State */}
      {showAnalyticsOnly && filteredAttempts.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Analytics Yet</h3>
              <p className="text-gray-500 mb-4">Complete some quizzes to see your performance analytics</p>
              <Button onClick={() => navigate('/student/quizzes')}>
                <FileQuestion className="w-4 h-4 mr-2" />
                View Quizzes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quiz List - Show on Dashboard and Quizzes pages */}
      {(showDashboard || showQuizzesOnly) && (
        <Card id="quiz-list">
          <CardHeader>
            <CardTitle>My Quizzes</CardTitle>
            <CardDescription>All available quizzes for your courses</CardDescription>
          </CardHeader>
          <CardContent>
            {quizzes.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No quizzes available yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quiz Title</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Completed At</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...quizzes]
                    .sort((a, b) => {
                      // Sort by pending first (not completed), then by title ascending
                      const aCompleted = isQuizCompleted(a.id);
                      const bCompleted = isQuizCompleted(b.id);
                      if (aCompleted !== bCompleted) {
                        return aCompleted ? 1 : -1; // Pending (not completed) first
                      }
                      return a.title.localeCompare(b.title); // Then by title ascending
                    })
                    .map((quiz) => {
                    const attempt = getQuizAttempt(quiz.id);
                    const completed = !!attempt;
                    const percentage = attempt && attempt.totalPoints > 0 
                      ? (attempt.score / attempt.totalPoints) * 100 
                      : 0;

                    return (
                      <TableRow key={quiz.id}>
                        <TableCell>{quiz.title}</TableCell>
                        <TableCell>{quiz.description}</TableCell>
                        <TableCell>{quiz.questions?.length || 0}</TableCell>
                        <TableCell>
                          {completed ? (
                            <Badge variant="default" className="bg-green-600">
                              Completed
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {attempt ? (
                            <span className="font-medium">
                              {attempt.score}/{attempt.totalPoints} ({percentage.toFixed(0)}%)
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {attempt ? (
                            <span className="text-sm text-gray-600">
                              {new Date(attempt.completedAt).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {completed ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/quiz/${quiz.id}/summary`)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Review
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/quiz/${quiz.id}`)}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Start Quiz
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}